import firebase from 'firebase/compat/app';
import 'firebase/compat/functions';
import { waitForAuth } from '../../../data/authUtils';
import type { OHLCVPoint } from './types';

interface FetchOptions {
    signal?: AbortSignal;
}

// Module-level cache for probability data keyed by "conditionId|startDate|endDate|intervalMinutes"
const probabilityCache = new Map<string, OHLCVPoint[]>();

export function cacheKey(
    conditionId: string,
    startDate: Date,
    endDate: Date,
    intervalMinutes: number,
    outcomeIndex: number
): string {
    return `${conditionId}|${startDate.toISOString().slice(0, 10)}|${endDate.toISOString().slice(0, 10)}|${intervalMinutes}|${outcomeIndex}`;
}

function normalizeRow(row: Record<string, unknown>): OHLCVPoint | null {
    const data = row.data as Record<string, number> | undefined;
    const ts = row.timestamp as string | undefined;

    // Flat shape: { date, open, high, low, close, volume, trade_count }
    if (!data && row.date) {
        const t = new Date(row.date as string);
        if (isNaN(t.getTime())) return null;
        return {
            t,
            open: (row.open as number) ?? (row.close as number) ?? 0,
            high: (row.high as number) ?? (row.close as number) ?? 0,
            low: (row.low as number) ?? (row.close as number) ?? 0,
            close: (row.close as number) ?? 0,
            vol:
                (row.volume_quote as number) ??
                (row.volume as number) ??
                (row.vol as number) ??
                0,
        };
    }

    // Nested shape: { timestamp, data: { open, high, low, close, volume } }
    if (!data || !ts) return null;

    const t = /[A-Za-z-]/.test(ts)
        ? new Date(ts)
        : new Date(Number(BigInt(ts) / BigInt(1_000_000)));

    if (isNaN(t.getTime())) return null;

    return {
        t,
        open: data.open ?? data.close ?? 0,
        high: data.high ?? data.close ?? 0,
        low: data.low ?? data.close ?? 0,
        close: data.close ?? 0,
        vol: data.volume ?? data.vol ?? 0,
    };
}

export async function loadMarketProbability(
    conditionId: string,
    startDate: Date,
    endDate: Date,
    intervalMinutes = 1440,
    options?: FetchOptions,
    outcomeIndex = 0
): Promise<OHLCVPoint[]> {
    const key = cacheKey(
        conditionId,
        startDate,
        endDate,
        intervalMinutes,
        outcomeIndex
    );
    const cached = probabilityCache.get(key);
    if (cached) return cached;

    await waitForAuth();

    if (options?.signal?.aborted)
        throw new DOMException('Aborted', 'AbortError');

    const functions = firebase.app().functions('europe-west6');
    const callable = functions.httpsCallable('loadEventPrices');

    const dayAfterEnd = new Date(endDate);
    dayAfterEnd.setUTCDate(dayAfterEnd.getUTCDate() + 1);

    const result = await callable({
        conditionId: conditionId,
        startDate: startDate.toISOString().slice(0, 10),
        endDate: dayAfterEnd.toISOString().slice(0, 10),
        intervalMinutes,
        outcomeIndex,
    });

    if (options?.signal?.aborted)
        throw new DOMException('Aborted', 'AbortError');

    const response = result.data as {
        rows?: unknown[];
        data?: unknown[];
        result?: { data?: unknown[] };
    };
    const rows = response.rows ?? response.data ?? response.result?.data ?? [];
    if (!Array.isArray(rows)) return [];

    const points = rows
        .map(r => normalizeRow(r as Record<string, unknown>))
        .filter((p): p is OHLCVPoint => p !== null)
        .sort((a, b) => a.t.getTime() - b.t.getTime());

    probabilityCache.set(key, points);
    if (points.length > 0) {
        console.log(
            `[sparkline] ${conditionId} | last point: ${points[points.length - 1].t.toISOString()} | requested end: ${endDate.toISOString()} | interval: ${intervalMinutes}min | count: ${points.length}`
        );
    }
    return points;
}

export interface MarketSeriesData {
    conditionId: string;
    data: OHLCVPoint[];
}

export async function loadMultiMarketProbability(
    conditionIds: string[],
    startDate: Date,
    endDate: Date,
    intervalMinutes = 1440,
    options?: FetchOptions
): Promise<MarketSeriesData[]> {
    const results = await Promise.allSettled(
        conditionIds.map(cid =>
            loadMarketProbability(
                cid,
                startDate,
                endDate,
                intervalMinutes,
                options
            )
        )
    );

    return conditionIds.map((cid, i) => ({
        conditionId: cid,
        data: results[i].status === 'fulfilled' ? results[i].value : [],
    }));
}
